// The Big Knock's next-event window (sprint 29 fix pass; spec: studio/sprints/29/BIGKNOCK-SPEC.md, "Fix pass").
// The gap rule is public: event n+1 starts 240 to 360 whole minutes after event n began (`after`, stored with the event, see scheduleOf).
// So "between A and B" is true by construction: A = after + 240 min rounded DOWN to the minute, B = after + 360 min rounded UP.
// The window never carries the drawn gap or the start, and it does not shrink as time passes. Pure, browser-safe, no Date, no clock.
import { BIGKNOCK } from './bigknock-core.js';
import { BIGKNOCK_COPY as C } from './bigknock-copy.js';

const MIN = 60000;

// { from, to } in ms for the event that follows the one that began at `after`. The real start always falls inside (inclusive).
export function windowOf(after) {
  const { min, max } = BIGKNOCK.gapMinutes;
  return { from: Math.floor((after + min * MIN) / MIN) * MIN, to: Math.ceil((after + max * MIN) / MIN) * MIN };
}
export const insideWindow = (win, startAt) => startAt >= win.from && startAt <= win.to;

// Local wall clock without Date: tzOffsetMin is Date#getTimezoneOffset() at that instant (UTC minus local, in minutes).
export function localClock(ms, tzOffsetMin = 0) {
  const m = Math.floor(ms / MIN) - tzOffsetMin, day = Math.floor(m / 1440), r = m - day * 1440;
  return { day, hh: Math.floor(r / 60), mm: r % 60 };
}
const two = n => String(n).padStart(2, '0');
export const hhmm = c => `${two(c.hh)}:${two(c.mm)}`;

// The line to show. state: 'ahead' (before the window opens), 'due' (inside it: any time now), 'late' (past it: the server was down).
// tzAt: ms -> getTimezoneOffset() at that instant (the client passes `ms => new Date(ms).getTimezoneOffset()`).
export function windowView(win, now, tzAt = () => 0) {
  const a = localClock(win.from, tzAt(win.from)), b = localClock(win.to, tzAt(win.to)), today = localClock(now, tzAt(now)).day;
  const da = a.day - today, db = b.day - today;
  const state = now > win.to ? 'late' : now >= win.from ? 'due' : 'ahead';
  let from = hhmm(a), to = hhmm(b), lead = '';
  if (da === db && da > 0) lead = C.window.day(da) + ' ';
  else { if (da > 0) from += ' ' + C.window.day(da); if (db > 0) to += ' ' + C.window.day(db); }
  return { state, from, to, lead };
}
// The same window stated exactly (sprint 31): the day is always named, and so is the time zone, zoneAt: ms -> a short zone name ("CEST", "GMT+2"; the page asks
// Intl). Still the public rule and nothing of the drawn start. Used by the page; windowLine above is the short form.
export function windowStated(win, now, n = 0, tzAt = () => 0, zoneAt = () => 'UTC') {
  const a = localClock(win.from, tzAt(win.from)), b = localClock(win.to, tzAt(win.to)), today = localClock(now, tzAt(now)).day;
  const dayWord = d => (d <= 0 ? 'today' : d === 1 ? 'tomorrow' : C.window.day(d)), zone = zoneAt(win.to);
  if (now > win.to) return C.window.late;
  if (now >= win.from) return C.window.stated.due({ n, to: hhmm(b) + (b.day > today ? ' ' + dayWord(b.day - today) : ''), zone });
  if (a.day === b.day) return C.window.stated.ahead({ n, day: dayWord(a.day - today) + ' ', from: hhmm(a), to: hhmm(b), zone });
  return C.window.stated.ahead({ n, from: `${hhmm(a)} ${dayWord(a.day - today)}`, to: `${hhmm(b)} ${dayWord(b.day - today)}`, zone });
}
export function windowLine(win, now, n = 0, tzAt = () => 0) {
  const v = windowView(win, now, tzAt);
  if (v.state === 'late') return C.window.late;
  return v.state === 'due' ? C.window.due(n, v.to) : C.window.line(n, v.lead, v.from, v.to);
}
